"use client";

import React, {
  createContext,
  useReducer,
  useEffect,
  useContext,
  ReactNode,
  Dispatch,
  useRef,
  useState,
} from "react";
import { usePathname } from "next/navigation";
import { toast } from "sonner";
import { Product, Variant } from "@/packages/types/products";
import { useAuth } from "./AuthContext";
import {
  CartItemApiPost,
  fetchUserCarts,
  toAddUserCart,
  toClearUserCart,
  toDeleteUserCart,
} from "@/api-service/cart";
import {
  cartsAreEqual,
  mergeCarts,
  transformApiCartItem,
  applySpecialDiscountsToCart,
} from "@/packages/utils/cart";
import { CartItem } from "../types/cart";
export type { CartItem };
import Cookies from "js-cookie";


// --- TYPES ---
interface CartState {
  cartItems: CartItem[];
  buyNowItems: CartItem[];
  couponResponse: unknown;
  lastAction?: { type: string; message?: string };
  previousOrderId: number | null;
}

type CartAction =
  | {
      type: "INIT_CART";
      payload: { cartItems: CartItem[]; buyNowItems: CartItem[]; previousOrderId?: number | null };
    }
  | {
      type: "ADD_TO_CART";
      payload: { productDetails: Product; variant?: Variant; quantity: number };
    }
  | {
      type: "REMOVE_FROM_CART";
      payload: { id: number | string; variant?: Variant };
    }
  | {
      type: "BUY_NOW";
      payload: { productDetails: Product; variant?: Variant; quantity: number };
    }
  | { type: "CLEAR_CART" }
  | {
      type: "REPLACE_CART";
      payload: { cartItems: CartItem[] };
    }
  | { type: "CLEAR_BUY_NOW" }
  | {
      type: "UPDATE_QUANTITY";
      payload: { id: number | string; variant?: Variant; quantity: number };
    }
  | { type: "SET_COUPON"; payload: unknown }
  | { type: "CLEAR_COUPON" }
  | { type: "RESET_CART_VIEW" }
  | { type: "SET_PREVIOUS_ORDER_ID"; payload: number | null };

// --- INITIAL STATE ---
const initialState: CartState = {
  cartItems: [],
  buyNowItems: [],
  couponResponse: null,
  previousOrderId: null,
};

// --- UTILITIES ---
const updateLocalStorage = (cartItems: CartItem[], buyNowItems: CartItem[]) => {
  if (typeof window !== "undefined") {
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
    localStorage.setItem("buyNowItems", JSON.stringify(buyNowItems));
  }
};

const getItemKey = (id: number | string, variantId?: number) =>
  `${id}-${variantId ?? "no-variant"}`;

// --- CONTEXT ---
export interface OrderProductItem {
  product?: Product;
  quantity?: number | string;
  variant?: Variant | null;
  [key: string]: unknown;
}

export interface CartContextType {
  cartItems: CartItem[];
  buyNowItems: CartItem[];
  couponResponse: unknown;
  previousOrderId: number | null;
  setPreviousOrderId: (id: number | null) => void;
  dispatch: Dispatch<CartAction>;
  addToCart: (
    product: Product,
    quantity?: number,
    variant?: Variant
  ) => Promise<void>;
  buyNow: (
    product: Product,
    quantity?: number,
    variant?: Variant
  ) => Promise<void>;
  updateCart: (
    productId: number | string,
    quantity: number,
    variant?: Variant
  ) => Promise<void>;
  removeCart: (
    productId: number | string,
    variant?: Variant
  ) => Promise<void>;
  clearCart: () => Promise<void>;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
  readdOrderToCart: (orderProducts: OrderProductItem[], orderId: number) => Promise<void>;
  replaceCartWithItems: (
    items: { product: Product; quantity: number; variant?: Variant }[]
  ) => Promise<void>;
}

const CartContext = createContext<CartContextType | null>(null);

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}

// --- REDUCER ---
const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case "INIT_CART":
      return { 
        ...state, 
        cartItems: action.payload.cartItems, 
        buyNowItems: action.payload.buyNowItems,
        previousOrderId: action.payload.previousOrderId !== undefined ? action.payload.previousOrderId : state.previousOrderId
      };

    case "SET_PREVIOUS_ORDER_ID":
      return { ...state, previousOrderId: action.payload };

    case "REMOVE_FROM_CART": {
      const { id, variant } = action.payload;

      const mainKey = getItemKey(id, variant?.id); // main item key
      const freeKey = `${mainKey}-free`; // free item key (variant-specific)

      const newCartItems = state.cartItems.filter((item) => {
        const itemKey = getItemKey(item.id, item.variant?.id);
        // remove main item and its exact free item only
        return itemKey !== mainKey && item.id !== freeKey;
      });

      updateLocalStorage(newCartItems, state.buyNowItems);

      return {
        ...state,
        cartItems: newCartItems,
        lastAction: { type: "INFO", message: "Product removed from cart" },
      };
    }

    case "BUY_NOW": {
      const { productDetails, variant, quantity } = action.payload;
      const availableStock = variant ? variant.stock : productDetails.stock;

      const productKey = getItemKey(productDetails.id, variant?.id);
      const freeKey = `${productKey}-free`;

      // Reuse discount calculation
      const calculateDiscount = (item: CartItem, totalQty: number) => {
        let discountAmount = 0;
        let finalPrice = item.price;

        if (
          item.specialDiscountStatus &&
          item.specialDiscount &&
          item.specialDiscount.discountType === 0
        ) {
          if (totalQty >= item.specialDiscount.minimumQuantity) {
            discountAmount = (item.price * item.specialDiscount.value) / 100;
            finalPrice = item.price - discountAmount;
          }
        }

        return { discountAmount, finalPrice };
      };

      //  Create main BuyNow item
      let newItem: CartItem = {
        productId: productDetails.id,
        ...productDetails,
        variant,
        quantity: Math.min(quantity, availableStock),
        price: variant ? Number(variant.salePrice) : productDetails.salePrice,
      };

      const { discountAmount, finalPrice } = calculateDiscount(
        newItem,
        newItem.quantity
      );
      newItem = { ...newItem, discountAmount, finalPrice };

      const newBuyNowItems: CartItem[] = [newItem];

      // Handle Free Item discount (discountType = 1)
      if (
        productDetails.specialDiscountStatus &&
        productDetails.specialDiscount &&
        productDetails.specialDiscount.discountType === 1
      ) {
        const freeQty =
          Math.floor(
            newItem.quantity / productDetails.specialDiscount.minimumQuantity
          ) * productDetails.specialDiscount.value;

        if (freeQty > 0) {
          const freeItem: CartItem = {
            ...newItem,
            id: freeKey,
            quantity: freeQty,
            price: 0,
            discountAmount: 0,
            finalPrice: 0,
            title: newItem.title + " (Free)",
          };
          newBuyNowItems.push(freeItem);
        }
      }

      updateLocalStorage(state.cartItems, newBuyNowItems);

      return {
        ...state,
        buyNowItems: newBuyNowItems,
        lastAction: { type: "SUCCESS", message: "Buy now initiated" },
      };
    }

    case "CLEAR_CART":
      updateLocalStorage([], state.buyNowItems);
      if (typeof window !== "undefined") {
        sessionStorage.removeItem("previousOrderId");
      }
      return {
        ...state,
        cartItems: [],
        previousOrderId: null,
      };

    case "REPLACE_CART":
      updateLocalStorage(action.payload.cartItems, []);
      if (typeof window !== "undefined") {
        sessionStorage.removeItem("previousOrderId");
      }
      return {
        ...state,
        cartItems: action.payload.cartItems,
        buyNowItems: [],
        previousOrderId: null,
      };

    case "CLEAR_BUY_NOW":
      updateLocalStorage(state.cartItems, []);
      return {
        ...state,
        buyNowItems: [],
        lastAction: { type: "INFO", message: "Buy now cleared" },
      };

    case "ADD_TO_CART": {
      const { productDetails, variant, quantity } = action.payload;
      const availableStock = variant ? variant.stock : productDetails.stock;

      const productKey = getItemKey(productDetails.id, variant?.id);
      const freeKey = `${productKey}-free`;

      const existingItem = state.cartItems.find(
        (item) => getItemKey(item.id, item.variant?.id) === productKey
      );

      const existingFreeItem = state.cartItems.find(
        (item) => item.id === freeKey
      );

      let newCartItems = [...state.cartItems];

      const calculateDiscount = (item: CartItem, totalQty: number) => {
        let discountAmount = 0;
        let finalPrice = item.price;

        if (
          item.specialDiscountStatus &&
          item.specialDiscount &&
          item.specialDiscount.discountType === 0
        ) {
          if (totalQty >= item.specialDiscount.minimumQuantity) {
            discountAmount = (item.price * item.specialDiscount.value) / 100;
            finalPrice = item.price - discountAmount;
          }
        }

        return { discountAmount, finalPrice };
      };

      // ✅ If item already exists
      if (existingItem) {
        const updatedQuantity = existingItem.quantity + quantity;

        if (updatedQuantity > availableStock) {
          return {
            ...state,
            lastAction: {
              type: "WARN",
              message: "Cannot add more than available stock",
            },
          };
        }

        newCartItems = newCartItems.map((item) => {
          if (getItemKey(item.id, item.variant?.id) === productKey) {
            const updatedItem = { ...item, quantity: updatedQuantity };
            const { discountAmount, finalPrice } = calculateDiscount(
              updatedItem,
              updatedQuantity
            ); // ✅ use total qty
            return { ...updatedItem, discountAmount, finalPrice };
          }
          return item;
        });

        // ✅ Update free item if discountType === 1
        if (
          productDetails.specialDiscountStatus &&
          productDetails.specialDiscount &&
          productDetails.specialDiscount.discountType === 1
        ) {
          const freeQty =
            Math.floor(
              updatedQuantity / productDetails.specialDiscount.minimumQuantity
            ) * productDetails.specialDiscount.value;

          if (existingFreeItem) {
            newCartItems = newCartItems.map((item) =>
              item.id === freeKey ? { ...item, quantity: freeQty } : item
            );
          } else if (freeQty > 0) {
            const freeItem: CartItem = {
              ...existingItem,
              id: freeKey,
              quantity: freeQty,
              price: 0,
              discountAmount: 0,
              finalPrice: 0,
              title: existingItem.title + " (Free)",
            };
            newCartItems.push(freeItem);
          }
        }
      }
      // ✅ If item is new
      else {
        if (availableStock === 0) {
          return {
            ...state,
            lastAction: {
              type: "WARN",
              message: "This product is out of stock",
            },
          };
        }

        let newItem: CartItem = {
          productId: productDetails.id,
          ...productDetails,
          variant,
          quantity: Math.min(quantity, availableStock),
          price: variant ? Number(variant.salePrice) : productDetails.salePrice,
        };

        const { discountAmount, finalPrice } = calculateDiscount(
          newItem,
          newItem.quantity
        );
        newItem = { ...newItem, discountAmount, finalPrice };

        newCartItems = [newItem, ...newCartItems];

        // ✅ Create free item if discountType === 1
        if (
          productDetails.specialDiscountStatus &&
          productDetails.specialDiscount &&
          productDetails.specialDiscount.discountType === 1
        ) {
          const freeQty =
            Math.floor(
              newItem.quantity / productDetails.specialDiscount.minimumQuantity
            ) * productDetails.specialDiscount.value;

          if (freeQty > 0) {
            const freeItem: CartItem = {
              ...newItem,
              id: freeKey,
              quantity: freeQty,
              price: 0,
              discountAmount: 0,
              finalPrice: 0,
              title: newItem.title + " (Free)",
            };
            newCartItems.push(freeItem);
          }
        }
      }

      updateLocalStorage(newCartItems, state.buyNowItems);
      return {
        ...state,
        cartItems: newCartItems,
        lastAction: { type: "SUCCESS", message: "Product added to cart" },
      };
    }

    case "UPDATE_QUANTITY": {
      const { id, variant, quantity } = action.payload;
      const productKey = getItemKey(id, variant?.id);
      const freeKey = `${productKey}-free`;

      let newCartItems = state.cartItems.map((item) =>
        getItemKey(item.id, item.variant?.id) === productKey
          ? { ...item, quantity }
          : item
      );

      // Find the original item again (after update)
      const originalItem = newCartItems.find(
        (item) => getItemKey(item.id, item.variant?.id) === productKey
      );

      const calculateDiscount = (item: CartItem) => {
        let discountAmount = 0;
        let finalPrice = item.price;

        if (
          item.specialDiscountStatus &&
          item.specialDiscount &&
          item.specialDiscount.discountType === 0
        ) {
          if (quantity >= item.specialDiscount.minimumQuantity) {
            discountAmount = (item.price * item.specialDiscount.value) / 100;
            finalPrice = item.price - discountAmount;
          }
        }

        return { discountAmount, finalPrice };
      };

      if (originalItem) {
        const { discountAmount, finalPrice } = calculateDiscount(originalItem);

        newCartItems = newCartItems.map((item) =>
          getItemKey(item.id, item.variant?.id) === productKey
            ? { ...item, discountAmount, finalPrice }
            : item
        );

        // Handle free items for discountType 1
        if (
          originalItem.specialDiscountStatus &&
          originalItem.specialDiscount &&
          originalItem.specialDiscount.discountType == 1
        ) {
          const freeQty =
            Math.floor(
              quantity / originalItem.specialDiscount.minimumQuantity
            ) * originalItem.specialDiscount.value;

          const existingFreeItem = newCartItems.find(
            (item) => item.id === freeKey
          );

          if (freeQty > 0) {
            if (existingFreeItem) {
              newCartItems = newCartItems.map((item) =>
                item.id === freeKey ? { ...item, quantity: freeQty } : item
              );
            } else {
              const freeItem: CartItem = {
                ...originalItem,
                id: freeKey,
                quantity: freeQty,
                price: 0,
                discountAmount: 0,
                finalPrice: 0,
                title: originalItem.title + " (Free)",
              };
              newCartItems.push(freeItem);
            }
          } else {
            // remove free item if qty is below minimum
            newCartItems = newCartItems.filter((item) => item.id !== freeKey);
          }
        }
      }

      updateLocalStorage(newCartItems, state.buyNowItems);
      return {
        ...state,
        cartItems: newCartItems,
        lastAction: { type: "SUCCESS", message: "Quantity updated" },
      };
    }

    case "SET_COUPON":
      return {
        ...state,
        couponResponse: action.payload,
        lastAction: { type: "SUCCESS", message: "Coupon applied" },
      };

    case "CLEAR_COUPON":
      return {
        ...state,
        couponResponse: null,
        lastAction: { type: "INFO", message: "Coupon removed" },
      };

    case "RESET_CART_VIEW":
      updateLocalStorage(state.cartItems, []);
      return {
        ...state,
        couponResponse: null,
        buyNowItems: [],
        lastAction: undefined,
      };

    default:
      return state;
  }
};

// --- PROVIDER ---
export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const pathname = usePathname();
  const isFirstMount = useRef(true);
  const { isLoggedIn, accessToken } = useAuth();

  const initialized = useRef(false);

  const formatCartItems = (items: CartItem[]): CartItemApiPost[] =>
    items.map((item) => ({
      productId: Number(item.productId),
      quantity: Number(item.quantity),
      variantId: item.variant?.id ? Number(item.variant.id) : null,
    }));

  const addToCartHandler = async (
    productDetails: Product,
    quantity: number = 1,
    variant?: Variant
  ) => {
    dispatch({
      type: "ADD_TO_CART",
      payload: { productDetails, variant, quantity },
    });

    if (isLoggedIn && accessToken) {
      const newItem = {
        productId: productDetails.id,
        quantity,
        variantId: variant?.id ?? null,
      };

      try {
        await toAddUserCart([newItem]);
      } catch (err) {
        console.error("Failed to sync new item to API", err);
        toast.error("Failed to add product to server cart");
      }
    }

    // Open side cart automatically after addition
    setIsCartOpen(true);
  };

  const UpdateCartHandler = async (
    id: number | string,
    quantity: number,
    variant?: Variant
  ) => {
    const item = state.cartItems.find(
      (i) => i.id === id && i.variant?.id === variant?.id
    );
    const availableStock = variant ? variant.stock : item?.stock ?? Infinity;
    if (quantity > availableStock) {
      toast.error(`Only ${availableStock} units available`);
      return;
    }
    console.log("Clearing cartssdds", isLoggedIn, accessToken);

    dispatch({
      type: "UPDATE_QUANTITY",
      payload: { id, variant, quantity },
    });

    if (isLoggedIn && accessToken) {
      const newItem = {
        productId: id,
        quantity,
        variantId: variant?.id ?? null,
      };

      try {
        await toAddUserCart([newItem]);
      } catch (err) {
        console.error("Failed to sync new item to API", err);
        toast.error("Failed to update product on server cart");
      }
    }
  };

  const RemoveCartHandler = async (id: number | string, variant?: Variant) => {
    dispatch({
      type: "REMOVE_FROM_CART",
      payload: { id, variant },
    });

    if (isLoggedIn && accessToken) {
      const newItem = {
        productId: id,
        variantId: variant?.id ?? null,
      };

      try {
        await toDeleteUserCart(newItem);
      } catch (err) {
        console.error("Failed to sync delete to API", err);
        toast.error("Failed to delete product from server cart");
      }
    }
  };

  const clearCart = async () => {
    dispatch({ type: "CLEAR_CART" });
    const token = Cookies.get("accessToken");

    console.log("Clearing cart", isLoggedIn, accessToken);

    if (token) {
      console.log("Clearing user cart on server", isLoggedIn, accessToken);
      try {
        await toClearUserCart();
      } catch (err) {
        console.error("Failed to sync delete to API", err);
        toast.error("Failed to delete product from server cart");
      }
    }
  };

  const buyNowHandler = async (
    productDetails: Product,
    quantity: number = 1,
    variant?: Variant
  ) => {
    dispatch({
      type: "BUY_NOW",
      payload: { productDetails, variant, quantity },
    });
  };

  const setPreviousOrderId = (orderId: number | null) => {
    dispatch({ type: "SET_PREVIOUS_ORDER_ID", payload: orderId });
    if (typeof window !== "undefined") {
      if (orderId) {
        sessionStorage.setItem("previousOrderId", String(orderId));
      } else {
        sessionStorage.removeItem("previousOrderId");
      }
    }
  };

  const readdOrderToCart = async (orderProducts: OrderProductItem[], orderId: number) => {
    // Set previous order ID
    setPreviousOrderId(orderId);

    for (const op of orderProducts) {
      if (op.product) {
        await addToCartHandler(op.product, Number(op.quantity || 1), op.variant || undefined);
      }
    }
    setIsCartOpen(false);
  };

  const replaceCartWithItems = async (
    items: { product: Product; quantity: number; variant?: Variant }[]
  ) => {
    const rawFormatted: CartItem[] = items.map((item) => {
      const { product: productDetails, quantity, variant } = item;
      const availableStock = variant ? variant.stock : (productDetails?.stock ?? Infinity);

      const priceVal = variant
        ? Number(variant.salePrice || productDetails.salePrice || 0)
        : Number(productDetails.salePrice || 0);

      const safePrice = isNaN(priceVal) ? 0 : priceVal;

      return {
        productId: productDetails.id,
        ...productDetails,
        variant: variant || undefined,
        quantity: Math.min(quantity, availableStock),
        price: safePrice,
        finalPrice: safePrice,
        discountAmount: 0,
      };
    });

    const finalCartItems = applySpecialDiscountsToCart(rawFormatted);

    dispatch({
      type: "REPLACE_CART",
      payload: { cartItems: finalCartItems },
    });

    const token = Cookies.get("accessToken") || accessToken;

    if (token) {
      try {
        await toClearUserCart();
        const apiPayload = finalCartItems
          .filter((item) => !String(item.id).includes("-free"))
          .map((item) => ({
            productId: Number(item.productId),
            quantity: Number(item.quantity),
            variantId: item.variant?.id ? Number(item.variant.id) : null,
          }));
        if (apiPayload.length > 0) {
          await toAddUserCart(apiPayload);
        }
      } catch (err) {
        console.error("Failed to sync replaced cart to server:", err);
      }
    }
  };


  useEffect(() => {
    const initCart = async () => {
      let localCart: CartItem[] = [];
      let localBuyNow: CartItem[] = [];
      let prevOrderIdVal: number | null = null;

      if (typeof window !== "undefined") {
        localCart = JSON.parse(localStorage.getItem("cartItems") || "[]");
        localBuyNow = JSON.parse(localStorage.getItem("buyNowItems") || "[]");
        const stored = sessionStorage.getItem("previousOrderId");
        prevOrderIdVal = stored ? Number(stored) : null;

        const urlParams = new URLSearchParams(window.location.search);
        const isAbandonedCartRecovery =
          urlParams.has("abandonedCartId") ||
          urlParams.has("cartId") ||
          urlParams.has("orderId") ||
          urlParams.has("recovery") ||
          urlParams.has("recoveryToken");

        if (isAbandonedCartRecovery) {
          localStorage.removeItem("cartItems");
          localStorage.removeItem("buyNowItems");
          dispatch({
            type: "INIT_CART",
            payload: {
              cartItems: [],
              buyNowItems: [],
              previousOrderId: urlParams.get("orderId")
                ? Number(urlParams.get("orderId"))
                : null,
            },
          });
          return;
        }
      }

      if (accessToken) {
        const apiCart = await fetchUserCarts();
        const apiCartItems = apiCart.data.map(transformApiCartItem);

        const mergedCart = mergeCarts(apiCartItems, localCart);

        dispatch({
          type: "INIT_CART",
          payload: { cartItems: mergedCart, buyNowItems: localBuyNow, previousOrderId: prevOrderIdVal },
        });

        const formattedCart = formatCartItems(mergedCart);

        if (!cartsAreEqual(apiCartItems, mergedCart)) {
          await toAddUserCart(formattedCart);
        }
      } else {
        dispatch({
          type: "INIT_CART",
          payload: { cartItems: localCart, buyNowItems: localBuyNow, previousOrderId: prevOrderIdVal },
        });
      }
    };

    initCart();
    initialized.current = true;
  }, [isLoggedIn, accessToken]);

  useEffect(() => {
    if (state.lastAction?.message) {
      const { type, message } = state.lastAction;
      if (type === "SUCCESS") toast.success(message);
      if (type === "WARN") toast.warning(message);
      if (type === "INFO") toast.info(message);
    }
  }, [state.lastAction]);

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    if (pathname === "/order-success") {
      dispatch({ type: "RESET_CART_VIEW" });
    }
  }, [pathname]);

  return (
    <CartContext.Provider
      value={{
        cartItems: state.cartItems,
        buyNowItems: state.buyNowItems,
        couponResponse: state.couponResponse,
        previousOrderId: state.previousOrderId,
        setPreviousOrderId,
        dispatch,
        addToCart: addToCartHandler,
        updateCart: UpdateCartHandler,
        removeCart: RemoveCartHandler,
        buyNow: buyNowHandler,
        clearCart: clearCart,
        isCartOpen,
        setIsCartOpen,
        readdOrderToCart,
        replaceCartWithItems,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
